<template>
    <div class="content w-full flex flex-col pt-16 justify-center transition-colors duration-300" :class="context.isDarkMode ? 'bg-void' : 'bg-white'">
        <LoadingDialog v-if="isLoading" :visible="isLoading" />

        <div v-if="clientModelValues.show" class="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
            <Dialog v-model:visible="clientModelValues.show" class="mx-2 md:mx-20" :draggable="false" 
                role="dialog" aria-label="Dados dos clientes"
                :closable="false" :pt:root:class="context.isDarkMode ? '!bg-void' : '!bg-white'" pt:mask:class="backdrop-blur-sm">
                <div class="rounded-lg p-6 text-center relative" :class="context.isDarkMode ? 'bg-void' : 'bg-white'">
                    <button class="absolute top-2 right-2 text-2xl md:block" :class="context.isDarkMode ? 'text-silver' : 'text-semidark-silver'"
                        @click="closeModal()">&times;</button>
                    
                    <Galleria 
                        containerStyle="max-width: 900px; margin-left: auto; margin-right: auto"
                        :value="clientModelValues.imageGallery" 
                        :responsiveOptions="responsiveGalleryOptions" 
                        :numVisible="4" 
                        :circular="true" 
                    >
                        <template #item="slotProps">
                            <img :src="slotProps.item.itemImageSrc" :alt="slotProps.item.alt" class="w-full h-auto block" height="450" width="800" />
                        </template>
                        <template #thumbnail="slotProps">
                            <img v-if="slotProps.item.thumbnailImageSrc != ''" :src="slotProps.item.thumbnailImageSrc" :alt="slotProps.item.alt" class="w-1/2 mx-auto md:w-full block h-auto" height="100" width="150" />
                        </template>
                    </Galleria>

                    <p class="text-2xl my-5 text-left font-normal" :class="context.isDarkMode ? 'text-silver' : 'text-void'">
                        {{ translationHandler.translate(clientModelValues.title) }}
                    </p>
                    <p 
                        class="my-5 text-xl italic font-semibold text-left" :class="context.isDarkMode ? 'text-silver' : 'text-void'"
                        v-html="translationHandler.translate(clientModelValues.description)"
                    >
                    </p>
                </div>
            </Dialog>
        </div>

        <HeroComponent />

        <ServiceComponent />

        <AboutComponent />

        <CaseComponent @selectedItem="openCaseInformations" />

        <BenefitsComponent />

        <TestimonyComponent />

        <FaqComponent />

        <SecondHeroComponent />

        <ContactComponent @openLoader="showLoader" @showSuportModal="alertSuport" />

        <Dialog v-model:visible="showSupportInformations" :style="{ width: '90%', maxWidth: '500px' }" :draggable="false" 
            role="dialog" aria-label="Informações de suporte"
            :closable="false" :pt:root:class="context.isDarkMode ? '!bg-void' : '!bg-white'" pt:mask:class="backdrop-blur-sm">
            <div class="rounded-lg p-6 text-center relative" :class="context.isDarkMode ? 'bg-void' : 'bg-white'">
                <button class="absolute top-2 right-2 text-2xl md:block" :class="context.isDarkMode ? 'text-silver' : 'text-void'"
                        @click="resetSupportData()">&times;</button>
                <div class="flex flex-col items-center justify-center space-y-4" :class="context.isDarkMode ? 'text-silver' : 'text-void'">
                    <i class="pi pi-exclamation-triangle text-6xl text-red-500"></i>

                    <h2 class="text-2xl font-semibold">
                        {{ translationHandler.translate('An error occurred', 'ucfirst') }}
                    </h2>

                    <p>
                        {{ translationHandler.translate("Don't worry! Your attempt to submit your quote failed, but the form has been **safely saved** and we have already notified our support team.", 'ucfirst') }}
                    </p>

                    <p class="text-sm font-light">
                        {{ translationHandler.translate('Our experts will take care of it and get back to you shortly.', 'ucfirst') }}
                    </p>
                </div>
            </div>
        </Dialog>
    </div>
</template>

<script setup>
import { defineAsyncComponent, onMounted, ref, watch } from 'vue';
import { useSessionMessageStore } from '@/stores/sessionMessage';
import { showToast } from '@/composables/toast';
import { Dialog, Galleria, useToast } from 'primevue';
import translationHandler from '@/composables/handlers/TranslationHandler';
import DomHandler from '@/composables/handlers/DomHandler';
import LoadingDialog from '@/components/Utils/LoadingDialog.vue';
import { useContextStore } from "@/stores/context";
import SupportService from '@/services/SupportService';

import HeroComponent from '@/components/HeroComponent.vue';

const ServiceComponent = defineAsyncComponent(() => import('@/components/ServiceComponent.vue'));
const AboutComponent = defineAsyncComponent(() => import('@/components/AboutComponent.vue'));
const CaseComponent = defineAsyncComponent(() => import('@/components/CaseComponent.vue'));
const TestimonyComponent = defineAsyncComponent(() => import('@/components/TestimonyComponent.vue'));
const BenefitsComponent = defineAsyncComponent(() => import('@/components/BenefitsComponent.vue'));
const FaqComponent = defineAsyncComponent(() => import('@/components/FaqComponent.vue'));
const SecondHeroComponent = defineAsyncComponent(() => import('@/components/SecondHeroComponent.vue'));
const ContactComponent = defineAsyncComponent(() => import('@/components/ContactComponent.vue'));

const sessionMessageStore = useSessionMessageStore();
const context = useContextStore();
const isLoading = ref(false);
const toast = useToast();

const clientModelValues = ref({
    title: '',
    image: '',
    imageGallery: [],
    description: '',
    show: false
});

watch(() => sessionMessageStore.hasMessage, (hasMsg) => {
    if (hasMsg) {
        showToast(toast, sessionMessageStore.getCurrentMessage, sessionMessageStore.getMessageType);
        sessionMessageStore.cleanMessage;
    }
}, { immediate: true });

onMounted(() => {
    DomHandler.enableScroll();
});

function openCaseInformations(selectedCase = null) {
    if (!selectedCase) return;

    const lang = context.getLang;
    let fullDesc = '';
    for (let key of Object.keys(selectedCase.fullDescription)) {
        const jsonData = selectedCase.fullDescription[key];
        fullDesc += jsonData[lang] + '<br><br>';
    }

    const data = {
        title: selectedCase.title,
        img: selectedCase.image,
        description: fullDesc,
        imageGallery: selectedCase.gallery
    }

    openModal(data);
}

function openModal(target = '') {
    let client = target;

    clientModelValues.value.title = client.title;
    clientModelValues.value.image = client.img;
    clientModelValues.value.description = client.description;
    clientModelValues.value.imageGallery = client.imageGallery ?? [];
    clientModelValues.value.show = true;

    DomHandler.disableScroll();
}

function closeModal() {
    clientModelValues.value.show = false; 
    DomHandler.enableScroll();
}

function showLoader(value) {
    const open = value == true;
    isLoading.value = open;
    if (open) {
        DomHandler.disableScroll();
    } else {
        DomHandler.enableScroll();
    }
}

const showSupportInformations = ref(false);
function alertSuport(dataPayload = {}) {
    showSupportInformations.value = true;
    SupportService.informSupportError(dataPayload);
    DomHandler.disableScroll();
}

function resetSupportData() {
    showSupportInformations.value = false;
    DomHandler.enableScroll();
}

const responsiveGalleryOptions = ref([
    { breakpoint: '1300px', numVisible: 4 },
    { breakpoint: '575px', numVisible: 1 }
]);
</script>

<style scoped>
.slide-enter-active,
.slide-leave-active {
    transition: all 0.3s ease;
}

.slide-enter-from,
.slide-leave-to {
    max-height: 0;
    opacity: 0;
    transform: translateY(-10px);
    overflow: hidden;
}

.slide-enter-to,
.slide-leave-from {
    max-height: 500px;
    opacity: 1;
    transform: translateY(0);
}
</style>